import React, { useState, useEffect, useCallback, useRef } from 'react';
import { paymentAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Search, RefreshCw, CheckCircle2, Clock, Loader } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

function SettlementBadge({ order, attempted }) {
  if (order.provider !== 'ablepay') {
    return <span className="badge" style={{ background: 'var(--cb-f1f5f9)', color: 'var(--text-muted)' }}>N/A ({order.provider})</span>;
  }
  const s = order.settlement;
  if (!s?.checkedAt) {
    // Still spinning only on the first pass — if the auto-check already ran for this row
    // (autoCheckedRef, below) and the result still isn't back, the check itself failed
    // silently (e.g. a transient AblePay error) rather than genuinely being "in flight", so
    // don't spin forever; fall back to Pending. The Refresh button retries these on demand.
    if (!attempted) {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.78rem', color: 'var(--text-muted)' }}>
          <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Checking…
        </span>
      );
    }
    return (
      <span className="badge badge-warning" style={{ display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}
        title="Couldn't confirm with AblePay yet — try Refresh Settlement Status">
        <Clock size={12} /> Pending
      </span>
    );
  }
  if (s.completed) {
    return (
      <span className="badge badge-success" style={{ display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}
        title={s.bankReference ? `Bank ref: ${s.bankReference}` : undefined}>
        <CheckCircle2 size={12} /> Settled {s.settlementDatetime ? format(new Date(s.settlementDatetime), 'dd/MM/yy HH:mm') : ''}
      </span>
    );
  }
  return (
    <span className="badge badge-warning" style={{ display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
      <Clock size={12} /> Pending
    </span>
  );
}

export default function OnlinePaymentsPage() {
  useDarkReady();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ page: 1, provider: '', settled: '', startDate: '', endDate: '', search: '' });
  const [bulkChecking, setBulkChecking] = useState(false);
  const autoCheckedRef = useRef(new Set());

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { ...filters, limit: 20 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await paymentAPI.getOnline(params);
      setOrders(data.data);
      setPagination(data.pagination);
    } catch { toast.error('Failed to load online payments'); }
    finally { if (!silent) setLoading(false); }
  }, [filters]);

  useEffect(() => { autoCheckedRef.current = new Set(); load(); }, [load]);

  // Settlement status isn't known until AblePay's Settlement Details API is actually called
  // (see backend RenewalOrder.settlement) — so any row that hasn't been checked yet gets
  // checked automatically right after the list loads, no button needed. Guarded by
  // autoCheckedRef so a silent reload afterwards doesn't re-trigger the same rows forever.
  useEffect(() => {
    const toCheck = orders
      .filter(o => o.provider === 'ablepay' && !o.settlement?.checkedAt && !autoCheckedRef.current.has(o._id))
      .map(o => o._id);
    if (!toCheck.length) return;
    toCheck.forEach(id => autoCheckedRef.current.add(id));
    (async () => {
      try {
        await paymentAPI.checkSettlement(toCheck);
        await load(true);
      } catch { /* rows just stay "Checking…"; the Refresh button below can retry */ }
    })();
  }, [orders, load]);

  const checkBacklog = async () => {
    setBulkChecking(true);
    try {
      const { data } = await paymentAPI.checkSettlement();
      toast.success(`Checked ${data.data.checked} order(s) — ${data.data.settled} settled`);
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk settlement check failed');
    } finally {
      setBulkChecking(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Online Payments</h2>
          <p className="text-muted text-small">Payment-gateway collections, with AblePay bank settlement status</p>
        </div>
        <button className="btn btn-secondary" onClick={checkBacklog} disabled={bulkChecking}
          title="Re-checks AblePay settlement status for orders still pending or not checked recently">
          <RefreshCw size={14} style={bulkChecking ? { animation: 'spin 1s linear infinite' } : undefined} /> {bulkChecking ? 'Checking…' : 'Refresh Settlement Status'}
        </button>
      </div>

      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search customer, mobile..."
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        </div>
        <input type="date" className="form-control" style={{ width: 150 }}
          value={filters.startDate} onChange={e => setFilters(f => ({ ...f, startDate: e.target.value, page: 1 }))} />
        <span style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>to</span>
        <input type="date" className="form-control" style={{ width: 150 }}
          value={filters.endDate} onChange={e => setFilters(f => ({ ...f, endDate: e.target.value, page: 1 }))} />
        <select className="form-control" style={{ width: 140 }}
          value={filters.provider} onChange={e => setFilters(f => ({ ...f, provider: e.target.value, page: 1 }))}>
          <option value="">All Gateways</option>
          <option value="ablepay">AblePay</option>
          <option value="razorpay">Razorpay</option>
        </select>
        <select className="form-control" style={{ width: 150 }}
          value={filters.settled} onChange={e => setFilters(f => ({ ...f, settled: e.target.value, page: 1 }))}>
          <option value="">Any Settlement</option>
          <option value="y">Settled</option>
          <option value="n">Not Settled</option>
        </select>
        <button className="btn btn-secondary" onClick={() => setFilters({ page: 1, provider: '', settled: '', startDate: '', endDate: '', search: '' })}>
          Clear
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Customer</th><th>For</th><th>Gateway</th><th>Txn ID</th>
                  <th>Amount</th><th>Receipt</th><th>Settlement</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o._id}>
                    <td style={{ fontSize: '.82rem' }}>{format(new Date(o.payment?.paymentDate || o.createdAt), 'dd/MM/yy HH:mm')}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{o.customer?.name}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{o.customer?.customerId}</div>
                    </td>
                    <td style={{ fontSize: '.8rem', textTransform: 'capitalize' }}>
                      {o.purpose === 'balance' ? 'Balance Payment' : `${o.serviceType} Renewal`}
                    </td>
                    <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{o.provider}</span></td>
                    <td className="font-mono" style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{o.providerTransactionId || '—'}</td>
                    <td style={{ fontWeight: 700, color: 'var(--success)' }}
                      title={o.settlement?.completed && o.settlement?.amountReimbursed != null ? 'Settled amount (net of AblePay TDR)' : 'Base amount — not yet settled'}>
                      ₹{fmt(o.settlement?.completed && o.settlement?.amountReimbursed != null ? o.settlement.amountReimbursed : o.amount)}
                    </td>
                    <td className="font-mono" style={{ fontSize: '.78rem' }}>{o.payment?.receiptNumber || '—'}</td>
                    <td>
                      <SettlementBadge order={o} attempted={autoCheckedRef.current.has(o._id)} />
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>No online payments found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginRight: 8 }}>
              Page {pagination.page} of {pagination.pages} · {pagination.total} total
            </span>
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>
    </div>
  );
}
